export interface IncidentRichText {
    text: string;
    renderAsHtml: boolean;
    inlineAttachmentIds?: string[];
    inlineAttachmentFileNames?: string[];
}

export interface JiraWikiAttachmentReference {
    id: string;
    fileName: string;
    contentType?: string | null;
}

export type JiraWikiRichText = IncidentRichText;

const ATNORTH_COMMENT_AUTHOR_MARKER_PATTERN = /^\[atnorth-comment-author:v\d+:[^\]\n]+\]$/gm;
const JSM_FILE_ATTACHMENT_MARKUP_LINE_PATTERN = /^\[\^[^\]\n]+\]\s*_\([^)\n]+\)_$/gm;
const ATTACHMENT_HASHED_FILENAME_PATTERN =
    /\s\(([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\)(\.[^.]+)$/i;
const JIRA_WIKI_HEADING_PATTERN = /^h([1-6])\.\s+(.+)$/;
const JIRA_WIKI_BULLET_PATTERN = /^\*\s+(.+)$/;
const JIRA_WIKI_ORDERED_PATTERN = /^#\s+(.+)$/;
const JIRA_WIKI_NESTED_BULLET_PATTERN = /^(\*+)\s+(.+)$/;
const JIRA_WIKI_NESTED_ORDERED_PATTERN = /^(#+)\s+(.+)$/;
const JIRA_WIKI_CODE_BLOCK_PATTERN = /\{code(?::([a-z0-9_-]+))?\}([\s\S]*?)\{code\}/gi;
const JIRA_WIKI_NOFORMAT_BLOCK_PATTERN = /\{noformat\}([\s\S]*?)\{noformat\}/gi;
const JIRA_WIKI_IMAGE_PATTERN = /!([^!\n]*\.[a-z0-9]{2,10}(?:\|[^!\n]+)?)!/gi;
const JIRA_WIKI_TABLE_HEADER_ROW_PATTERN = /^\|\|.*\|\|$/;
const JIRA_WIKI_TABLE_ROW_PATTERN = /^\|.*\|$/;
const JIRA_WIKI_TASK_ITEM_PATTERN = /^-(.+)-$/;
const JIRA_WIKI_FORMATTING_PATTERN =
    /(^|\n)(?:h[1-6]\.\s+.+|[*#]+\s+.+|\|\|.*\|\||\|.*\|)|\{(?:code(?::[a-z0-9_-]+)?|noformat)\}[\s\S]*?\{(?:code|noformat)\}|\{color:#[0-9a-fA-F]{6}\}[\s\S]*?\{color\}|\[[^\]|]+\|[^\]\n]+\]|![^!\n]*\.[a-z0-9]{2,10}(?:\|[^!\n]+)?!|(^|[\s(])(?:_(?!_)[^_\n]+_(?!_)|[*](?!\s)[^*\n]+[*](?![*])|[+](?![+])[^+\n]+[+](?![+])|-(?!-)[^-\n]+-(?!-)|[\^](?![\^])[^^\n]+[\^](?![\^])|~(?![~])[^~\n]+~(?![~]))(?=$|[\s).,!?;:])/im;

const JIRA_WIKI_INLINE_FORMATTING_TAGS = {
    '*': 'strong',
    _: 'em',
    '+': 'u',
    '-': 's',
    '^': 'sup',
    '~': 'sub',
} as const;

type JiraWikiInlineDelimiter = keyof typeof JIRA_WIKI_INLINE_FORMATTING_TAGS;

type JiraWikiRenderOptions = {
    attachments?: JiraWikiAttachmentReference[];
};

type JiraWikiRenderResult = {
    html: string;
    inlineAttachmentIds: string[];
    inlineAttachmentFileNames: string[];
};

export function normalizeJiraWikiText(text: string): string {
    return text.replaceAll('\r\n', '\n').replaceAll('\r', '\n');
}

function sanitizeHtmlHref(href: unknown): string | null {
    if (typeof href !== 'string' || href.length === 0) return null;

    return /^(https?:\/\/\S+|mailto:\S+|\/\S*)$/i.test(href) ? href : null;
}

function sanitizeHtmlColor(color: unknown): string | null {
    if (typeof color !== 'string') return null;

    return /^#[0-9a-fA-F]{6}$/.test(color) ? color.toLowerCase() : null;
}

function escapeHtml(text: string): string {
    return text
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}

function renderJiraWikiCodeBlockHtml(code: string, language?: string): string {
    const escapedCode = escapeHtml(code.trim());
    if (!escapedCode) return '';

    const normalizedLanguage =
        typeof language === 'string' && /^[a-z0-9_-]+$/i.test(language) ? language.toLowerCase() : '';
    const className = normalizedLanguage ? ` class="language-${normalizedLanguage}"` : '';

    return `<pre><code${className}>${escapedCode}</code></pre>`;
}

function normalizeJiraWikiAttachmentName(fileName: string): string {
    return fileName.trim().replace(ATTACHMENT_HASHED_FILENAME_PATTERN, '$2');
}

function isJiraWikiImageAttachment(attachment: JiraWikiAttachmentReference): boolean {
    if (attachment.contentType?.startsWith('image/')) return true;

    const extension = attachment.fileName.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension ?? '');
}

function parseJiraWikiImageOptions(
    optionsText: string | undefined,
    fallbackAlt: string,
): { alt: string; width?: string } {
    const parsed = {
        alt: fallbackAlt,
        width: undefined as string | undefined,
    };

    if (!optionsText) return parsed;

    for (const rawOption of optionsText.split(',')) {
        const option = rawOption.trim();
        if (!option) continue;

        const widthMatch = option.match(/^width=(\d+)$/i);
        if (widthMatch?.[1]) {
            parsed.width = widthMatch[1];
            continue;
        }

        const altMatch = option.match(/^alt=(?:"([^"]*)"|'([^']*)'|(.*))$/i);
        const altValue = altMatch?.[1] ?? altMatch?.[2] ?? altMatch?.[3];
        if (typeof altValue === 'string' && altValue.trim()) {
            parsed.alt = altValue.trim();
        }
    }

    return parsed;
}

function renderJiraWikiImageHtml(
    imageMarkup: string,
    attachments: JiraWikiAttachmentReference[],
    inlineAttachmentIds: Set<string>,
    inlineAttachmentFileNames: string[],
): string {
    const pipeIndex = imageMarkup.indexOf('|');
    const rawFileName = pipeIndex === -1 ? imageMarkup : imageMarkup.slice(0, pipeIndex);
    const optionsText = pipeIndex === -1 ? undefined : imageMarkup.slice(pipeIndex + 1);
    const normalizedFileName = normalizeJiraWikiAttachmentName(rawFileName);

    if (!normalizedFileName) return '';

    const isBackendThumbnailMarkup = optionsText
        ?.split(',')
        .some((option) => option.trim().toLowerCase() === 'thumbnail');

    if (isBackendThumbnailMarkup && inlineAttachmentFileNames.includes(normalizedFileName.toLowerCase())) {
        return '';
    }

    const matchingAttachments = attachments.filter(
        (candidate) =>
            candidate.id &&
            isJiraWikiImageAttachment(candidate) &&
            normalizeJiraWikiAttachmentName(candidate.fileName).toLowerCase() === normalizedFileName.toLowerCase(),
    );
    const attachment =
        matchingAttachments.find((candidate) => !inlineAttachmentIds.has(String(candidate.id))) ??
        matchingAttachments[0];

    if (!attachment?.id) return '';

    const normalizedAttachmentId = String(attachment.id);
    const isFirstReferenceToAttachment = !inlineAttachmentIds.has(normalizedAttachmentId);
    inlineAttachmentIds.add(normalizedAttachmentId);
    if (isFirstReferenceToAttachment) {
        inlineAttachmentFileNames.push(normalizeJiraWikiAttachmentName(attachment.fileName).toLowerCase());
    }

    const { alt, width } = parseJiraWikiImageOptions(optionsText, attachment.fileName);
    const widthAttribute = width ? ` data-inline-width="${escapeHtml(width)}"` : '';

    return `<figure class="comment-inline-image" data-inline-attachment-id="${escapeHtml(normalizedAttachmentId)}" data-inline-alt="${escapeHtml(alt)}"${widthAttribute}></figure>`;
}

function extractJiraWikiBlockPlaceholders(
    text: string,
    attachments: JiraWikiAttachmentReference[],
): {
    text: string;
    placeholders: Map<string, string>;
    inlineAttachmentIds: string[];
    inlineAttachmentFileNames: string[];
} {
    const placeholders = new Map<string, string>();
    const inlineAttachmentIds = new Set<string>();
    const inlineAttachmentFileNames: string[] = [];
    let index = 0;

    function storePlaceholder(html: string): string {
        if (!html) return '';

        const token = `__JIRA_WIKI_BLOCK_${index}__`;
        index += 1;
        placeholders.set(token, html);
        return token;
    }

    const withCodeBlocks = text.replace(JIRA_WIKI_CODE_BLOCK_PATTERN, (_, language: string | undefined, code: string) =>
        storePlaceholder(renderJiraWikiCodeBlockHtml(code, language)),
    );

    const withNoformatBlocks = withCodeBlocks.replace(JIRA_WIKI_NOFORMAT_BLOCK_PATTERN, (_, code: string) =>
        storePlaceholder(renderJiraWikiCodeBlockHtml(code)),
    );

    const withImageBlocks = withNoformatBlocks.replace(JIRA_WIKI_IMAGE_PATTERN, (_, imageMarkup: string) =>
        storePlaceholder(
            renderJiraWikiImageHtml(imageMarkup, attachments, inlineAttachmentIds, inlineAttachmentFileNames),
        ),
    );

    return {
        text: withImageBlocks,
        placeholders,
        inlineAttachmentIds: [...inlineAttachmentIds],
        inlineAttachmentFileNames,
    };
}

function splitJiraWikiTableRow(line: string, isHeader: boolean): string[] {
    const delimiter = isHeader ? '||' : '|';
    return line
        .slice(delimiter.length, -delimiter.length)
        .split(delimiter)
        .map((cell) => cell.trim());
}

function isJiraWikiInlineDelimiter(char: string | undefined): char is JiraWikiInlineDelimiter {
    return Boolean(char && char in JIRA_WIKI_INLINE_FORMATTING_TAGS);
}

function isJiraWikiInlineOpenBoundary(char: string | undefined): boolean {
    return !char || /\s/.test(char) || '([{'.includes(char) || isJiraWikiInlineDelimiter(char);
}

function isJiraWikiInlineCloseBoundary(char: string | undefined): boolean {
    return !char || /\s/.test(char) || ')]}.,!?;:'.includes(char) || isJiraWikiInlineDelimiter(char);
}

function canOpenJiraWikiInlineFormatting(text: string, index: number, delimiter: JiraWikiInlineDelimiter): boolean {
    const previous = text[index - 1];
    const next = text[index + 1];

    return (
        text[index] === delimiter &&
        Boolean(next) &&
        next !== '\n' &&
        !/\s/.test(next) &&
        isJiraWikiInlineOpenBoundary(previous)
    );
}

function canCloseJiraWikiInlineFormatting(text: string, index: number, delimiter: JiraWikiInlineDelimiter): boolean {
    const previous = text[index - 1];
    const next = text[index + 1];

    return (
        text[index] === delimiter &&
        Boolean(previous) &&
        previous !== '\n' &&
        !/\s/.test(previous) &&
        isJiraWikiInlineCloseBoundary(next)
    );
}

function renderJiraWikiLinkHtml(label: string, href: string): string {
    const trimmedLabel = label.trim();
    if (!trimmedLabel) return '';

    const safeHref = sanitizeHtmlHref(href.trim());
    if (!safeHref) {
        return `<span class="comment-link comment-link--invalid">${escapeHtml(trimmedLabel)}</span>`;
    }

    return `<a href="${escapeHtml(safeHref)}" target="_blank" rel="noopener noreferrer">${escapeHtml(trimmedLabel)}</a>`;
}

function matchJiraWikiInlineLink(text: string, index: number): { html: string; end: number } | null {
    const match = text.slice(index).match(/^\[([^\]|]+)\|([^\]\n]+)\]/);
    if (!match?.[0]) return null;

    return {
        html: renderJiraWikiLinkHtml(match[1] ?? '', match[2] ?? ''),
        end: index + match[0].length,
    };
}

function matchJiraWikiInlineColor(text: string, index: number): { html: string; end: number } | null {
    const match = text.slice(index).match(/^\{color:(#[0-9a-fA-F]{6})\}([\s\S]*?)\{color\}/);
    if (!match?.[0]) return null;

    const safeColor = sanitizeHtmlColor(match[1] ?? '');
    if (!safeColor) return null;

    return {
        html: `<span style="color:${safeColor}">${renderJiraWikiInlineFormatting(match[2] ?? '').html}</span>`,
        end: index + match[0].length,
    };
}

function renderJiraWikiInlineFormatting(
    text: string,
    startIndex = 0,
    stopDelimiter?: JiraWikiInlineDelimiter,
): { html: string; index: number; closed: boolean } {
    let html = '';
    let plainText = '';
    let index = startIndex;

    const flushPlainText = () => {
        if (!plainText) return;
        html += escapeHtml(plainText);
        plainText = '';
    };

    while (index < text.length) {
        const current = text[index];

        if (
            stopDelimiter &&
            current === stopDelimiter &&
            canCloseJiraWikiInlineFormatting(text, index, stopDelimiter)
        ) {
            flushPlainText();
            return {
                html,
                index: index + 1,
                closed: true,
            };
        }

        const linkMatch = matchJiraWikiInlineLink(text, index);
        if (linkMatch) {
            flushPlainText();
            html += linkMatch.html;
            index = linkMatch.end;
            continue;
        }

        const colorMatch = matchJiraWikiInlineColor(text, index);
        if (colorMatch) {
            flushPlainText();
            html += colorMatch.html;
            index = colorMatch.end;
            continue;
        }

        if (current && isJiraWikiInlineDelimiter(current) && canOpenJiraWikiInlineFormatting(text, index, current)) {
            flushPlainText();

            const inner = renderJiraWikiInlineFormatting(text, index + 1, current);
            if (inner.closed) {
                const tag = JIRA_WIKI_INLINE_FORMATTING_TAGS[current];
                html += `<${tag}>${inner.html}</${tag}>`;
                index = inner.index;
                continue;
            }
        }

        plainText += current ?? '';
        index += 1;
    }

    flushPlainText();
    return {
        html,
        index,
        closed: false,
    };
}

function applyJiraWikiInlineFormatting(text: string): string {
    return renderJiraWikiInlineFormatting(text).html;
}

function splitJiraWikiPlaceholderFragments(
    text: string,
    placeholders: Map<string, string>,
): Array<{ type: 'text' | 'html'; value: string }> {
    if (!text || placeholders.size === 0) {
        return text ? [{ type: 'text', value: text }] : [];
    }

    return text
        .split(/(__JIRA_WIKI_BLOCK_\d+__)/g)
        .filter((segment) => segment.length > 0)
        .map((segment) =>
            placeholders.has(segment)
                ? { type: 'html' as const, value: placeholders.get(segment) ?? '' }
                : { type: 'text' as const, value: segment },
        )
        .filter((segment) => segment.value.length > 0);
}

function convertJiraWikiParagraphBlockToHtml(lines: string[], placeholders: Map<string, string>): string {
    const paragraphText = lines.join('\n');
    const fragments = splitJiraWikiPlaceholderFragments(paragraphText, placeholders);

    if (fragments.length === 0) return '';

    return fragments
        .map((fragment) => {
            if (fragment.type === 'html') {
                return fragment.value;
            }

            const text = fragment.value
                .split('\n')
                .map((line) => applyJiraWikiInlineFormatting(line))
                .join('<br>');
            return text ? `<p>${text}</p>` : '';
        })
        .filter((fragment) => fragment.length > 0)
        .join('');
}

function renderBlankJiraWikiParagraph(): string {
    return '<p><br></p>';
}

function convertJiraWikiListBlockToHtml(lines: string[], ordered: boolean): string {
    const tag = ordered ? 'ol' : 'ul';
    const items = lines
        .map((line) => line.replace(ordered ? JIRA_WIKI_ORDERED_PATTERN : JIRA_WIKI_BULLET_PATTERN, '$1'))
        .map((line) => `<li>${applyJiraWikiInlineFormatting(line)}</li>`)
        .join('');

    return items ? `<${tag}>${items}</${tag}>` : '';
}

function convertJiraWikiNestedListBlockToHtml(lines: string[], ordered: boolean): string {
    const nestedPattern = ordered ? JIRA_WIKI_NESTED_ORDERED_PATTERN : JIRA_WIKI_NESTED_BULLET_PATTERN;
    const tag = ordered ? 'ol' : 'ul';
    const items = lines
        .map((line) => line.trim())
        .map((line) => {
            const match = line.match(nestedPattern);
            if (!match) return null;

            return {
                level: match[1]?.length ?? 1,
                text: match[2] ?? '',
            };
        })
        .filter((item): item is { level: number; text: string } => item !== null);

    if (items.length === 0) return '';

    function renderLevel(startIndex: number, level: number): { html: string; nextIndex: number } {
        let html = `<${tag}>`;
        let index = startIndex;

        while (index < items.length) {
            const item = items[index]!;
            if (item.level < level) break;

            if (item.level > level) {
                const nested = renderLevel(index, item.level);
                html += nested.html;
                index = nested.nextIndex;
                continue;
            }

            index += 1;
            let nestedHtml = '';
            if (index < items.length && items[index]!.level > level) {
                const nested = renderLevel(index, items[index]!.level);
                nestedHtml = nested.html;
                index = nested.nextIndex;
            }

            html += `<li>${applyJiraWikiInlineFormatting(item.text)}${nestedHtml}</li>`;
        }

        html += `</${tag}>`;
        return { html, nextIndex: index };
    }

    return renderLevel(0, items[0]!.level).html;
}

function convertJiraWikiTableBlockToHtml(lines: string[]): string {
    const headerRows = lines.filter((line) => JIRA_WIKI_TABLE_HEADER_ROW_PATTERN.test(line));
    const bodyRows = lines.filter(
        (line) => JIRA_WIKI_TABLE_ROW_PATTERN.test(line) && !JIRA_WIKI_TABLE_HEADER_ROW_PATTERN.test(line),
    );

    const thead = headerRows[0]
        ? `<thead><tr>${splitJiraWikiTableRow(headerRows[0], true)
              .map((cell) => `<th>${applyJiraWikiInlineFormatting(cell)}</th>`)
              .join('')}</tr></thead>`
        : '';
    const tbodyRows = bodyRows
        .map((line) => {
            const cells = splitJiraWikiTableRow(line, false)
                .map((cell) => `<td>${applyJiraWikiInlineFormatting(cell)}</td>`)
                .join('');
            return cells ? `<tr>${cells}</tr>` : '';
        })
        .filter((row) => row.length > 0)
        .join('');
    const tbody = tbodyRows ? `<tbody>${tbodyRows}</tbody>` : '';

    return thead || tbody ? `<table>${thead}${tbody}</table>` : '';
}

function convertJiraWikiTaskListBlockToHtml(lines: string[]): string {
    const items = lines
        .map((line) => line.trim().replace(JIRA_WIKI_BULLET_PATTERN, '$1').trim())
        .map((line) => {
            const checkedMatch = line.match(JIRA_WIKI_TASK_ITEM_PATTERN);
            const checked = Boolean(checkedMatch);
            const text = checked ? (checkedMatch?.[1]?.trim() ?? '') : line;
            const checkedAttribute = checked ? ' checked' : '';
            return text
                ? `<li><label><input type="checkbox"${checkedAttribute} disabled> ${applyJiraWikiInlineFormatting(text)}</label></li>`
                : '';
        })
        .filter((item) => item.length > 0)
        .join('');

    return items ? `<ul class="comment-task-list">${items}</ul>` : '';
}

function convertJiraWikiBlockToHtml(lines: string[], placeholders: Map<string, string>): string {
    if (lines.length === 0) return '';

    const firstLine = lines[0]!.trim();
    const headingMatch = firstLine.match(JIRA_WIKI_HEADING_PATTERN);
    if (headingMatch && lines.length === 1) {
        const level = headingMatch[1];
        const text = applyJiraWikiInlineFormatting(headingMatch[2] ?? '');
        return `<h${level}>${text}</h${level}>`;
    }

    if (
        lines.every((line) => JIRA_WIKI_BULLET_PATTERN.test(line.trim())) &&
        lines.some((line) =>
            JIRA_WIKI_TASK_ITEM_PATTERN.test(line.trim().replace(JIRA_WIKI_BULLET_PATTERN, '$1').trim()),
        )
    ) {
        return convertJiraWikiTaskListBlockToHtml(lines);
    }

    if (lines.every((line) => JIRA_WIKI_NESTED_BULLET_PATTERN.test(line.trim()))) {
        return lines.some((line) => line.trim().startsWith('**'))
            ? convertJiraWikiNestedListBlockToHtml(lines, false)
            : convertJiraWikiListBlockToHtml(
                  lines.map((line) => line.trim()),
                  false,
              );
    }

    if (lines.every((line) => JIRA_WIKI_NESTED_ORDERED_PATTERN.test(line.trim()))) {
        return lines.some((line) => line.trim().startsWith('##'))
            ? convertJiraWikiNestedListBlockToHtml(lines, true)
            : convertJiraWikiListBlockToHtml(
                  lines.map((line) => line.trim()),
                  true,
              );
    }

    if (
        lines.every((line) => JIRA_WIKI_TABLE_HEADER_ROW_PATTERN.test(line) || JIRA_WIKI_TABLE_ROW_PATTERN.test(line))
    ) {
        return convertJiraWikiTableBlockToHtml(lines);
    }

    return convertJiraWikiParagraphBlockToHtml(lines, placeholders);
}

export function sanitizeIncidentCommentText(text: string): string {
    return normalizeJiraWikiText(text)
        .replace(ATNORTH_COMMENT_AUTHOR_MARKER_PATTERN, '')
        .replace(JSM_FILE_ATTACHMENT_MARKUP_LINE_PATTERN, '')
        .trim();
}

function splitJiraWikiBlocksPreservingBlankLines(text: string): string[] {
    const blocks: string[] = [];
    let blockStart = 0;
    const blockDelimiterPattern = /\n{2,}/g;

    for (const match of text.matchAll(blockDelimiterPattern)) {
        const delimiter = match[0];
        const delimiterIndex = match.index ?? 0;
        const block = text.slice(blockStart, delimiterIndex);

        if (block) {
            blocks.push(block);
        }

        const extraBlankParagraphs = Math.max(delimiter.length - 2, 0);
        for (let index = 0; index < extraBlankParagraphs; index += 1) {
            blocks.push('');
        }

        blockStart = delimiterIndex + delimiter.length;
    }

    const trailingBlock = text.slice(blockStart);
    if (trailingBlock) {
        blocks.push(trailingBlock);
    }

    return blocks;
}

function renderJiraWikiToHtml(text: string, options?: JiraWikiRenderOptions): JiraWikiRenderResult {
    const cleanedText = sanitizeIncidentCommentText(text);
    if (!cleanedText) return { html: '', inlineAttachmentIds: [], inlineAttachmentFileNames: [] };

    const normalized = cleanedText.replaceAll('\r\n', '\n');
    const attachments = options?.attachments ?? [];
    const {
        text: placeholderText,
        placeholders,
        inlineAttachmentIds,
        inlineAttachmentFileNames,
    } = extractJiraWikiBlockPlaceholders(normalized, attachments);
    const blocks = splitJiraWikiBlocksPreservingBlankLines(placeholderText);

    const html = blocks
        .map((block) => {
            if (block.length === 0) return renderBlankJiraWikiParagraph();

            const lines = block.split('\n').map((line) => line.trimEnd());
            const token = lines.length === 1 ? (lines[0]?.trim() ?? '') : '';
            return token && placeholders.has(token)
                ? (placeholders.get(token) ?? '')
                : convertJiraWikiBlockToHtml(lines, placeholders);
        })
        .filter((block) => block.length > 0)
        .join('');

    return {
        html,
        inlineAttachmentIds,
        inlineAttachmentFileNames,
    };
}

export function convertJiraWikiToHtml(text: string, options?: JiraWikiRenderOptions): string {
    return renderJiraWikiToHtml(text, options).html;
}

export function looksLikeJiraWikiMarkup(text: string): boolean {
    const cleanedText = sanitizeIncidentCommentText(text);
    if (!cleanedText) return false;

    return JIRA_WIKI_FORMATTING_PATTERN.test(cleanedText);
}

export function resolveJiraWikiRichText(
    text: string | null | undefined,
    options?: JiraWikiRenderOptions,
): JiraWikiRichText | null {
    if (!text || !looksLikeJiraWikiMarkup(text)) return null;

    const { html, inlineAttachmentIds, inlineAttachmentFileNames } = renderJiraWikiToHtml(text, options);
    if (!html) return null;

    return {
        text: html,
        renderAsHtml: true,
        ...(inlineAttachmentIds.length > 0 ? { inlineAttachmentIds } : {}),
        ...(inlineAttachmentFileNames.length > 0 ? { inlineAttachmentFileNames } : {}),
    };
}
